import { useState, useMemo } from "react";
import api from "../../utils/api";
import { calcRequiredFTE, calcLocalization, calcEffectiveHours } from "../../utils/calculations";
import { Plus, Trash2, RefreshCw, ChevronDown, ChevronUp, Info } from "lucide-react";

const STAGE_TYPES = ["Front Office", "Middle Office", "Back Office", "Support"];
const PRIORITIES = ["Critical", "High", "Medium", "Low"];
const DOMAINS = ["Insurance", "Banking & Fintech", "Healthcare / BPO", "Telecom", "Retail / eCommerce", "Logistics", "Government Services", "Other / Generic", "Custom"];

const priorityColors = { Critical: "bg-red-100 text-red-700", High: "bg-orange-100 text-orange-700", Medium: "bg-yellow-100 text-yellow-700", Low: "bg-sage-100 text-sage-600" };
const typeColors = { "Front Office": "bg-blue-50 text-blue-700", "Middle Office": "bg-purple-50 text-purple-700", "Back Office": "bg-sage-50 text-sage-700", "Support": "bg-gray-50 text-gray-600" };

function FlagChip({ flag, diff }) {
  if (flag === "ok") return <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ On target</span>;
  if (flag === "over") return <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">▲ +{Math.abs(diff)}pp</span>;
  return <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">▼ -{Math.abs(diff)}pp</span>;
}

export default function ProcessStagesTab({ project, updateStages, effectiveHours }) {
  const setup = project?.setup || {};
  const org = setup.org_profile || {};
  const vc = setup.volume_capacity || {};
  const stages = project?.stages || [];
  const totalVolume = vc.total_monthly_volume || 0;
  const eff = effectiveHours || calcEffectiveHours(setup.effective_hours);

  const [loadingTemplate, setLoadingTemplate] = useState(false);

  const stagesWithCalc = useMemo(() => {
    return stages.map((s) => ({
      ...s,
      reqFTE: calcRequiredFTE(s, totalVolume, eff),
      loc: calcLocalization(s, org, eff),
    }));
  }, [stages, totalVolume, eff, org]);

  const totalLocalCount = stages.reduce((a, s) => a + (s.local_count || 0), 0);
  const totalExpatCount = stages.reduce((a, s) => a + (s.expat_count || 0), 0);
  const totalTeam = totalLocalCount + totalExpatCount;
  const totalReqFTE = stagesWithCalc.reduce((a, s) => a + s.reqFTE, 0);
  const overallDelta = totalTeam - totalReqFTE;
  const overallLocalPct = totalTeam > 0 ? (totalLocalCount / totalTeam * 100).toFixed(1) : 0;
  const avgYield = stages.length > 0 ? (stages.reduce((a, s) => a + (s.yield_pct || 100), 0) / stages.length).toFixed(0) : 0;

  const updateStage = (idx, key, val) => {
    const updated = stages.map((s, i) => i === idx ? { ...s, [key]: val } : s);
    updateStages(updated);
  };

  const addStage = () => {
    const newStage = {
      id: Date.now().toString(),
      stage_name: `Stage ${stages.length + 1}`,
      yield_pct: 100,
      aht: 10,
      stage_type: "Front Office",
      priority: "Medium",
      local_count: 1,
      expat_count: 4,
      utilization: 75,
      productivity: 80,
    };
    updateStages([...stages, newStage]);
  };

  const removeStage = (idx) => {
    updateStages(stages.filter((_, i) => i !== idx));
  };

  const loadTemplate = async () => {
    const domain = org.domain || "Other / Generic";
    setLoadingTemplate(true);
    try {
      const res = await api.get("/domains");
      const tmpl = res.data.templates?.[domain]?.stages;
      if (tmpl) {
        const withIds = tmpl.map((s, i) => ({ ...s, id: `tmpl-${Date.now()}-${i}` }));
        updateStages(withIds);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingTemplate(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Step 2</p>
          <h2 className="font-heading text-2xl font-semibold text-sage-900">Process Stages</h2>
          <p className="text-sage-500 text-sm mt-1">Configure workflow stages, headcount, and per-stage parameters. FTE calculations update in real-time.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="load-template-button" onClick={loadTemplate} disabled={loadingTemplate} className="flex items-center gap-1.5 border border-sage-200 text-sage-600 rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-sage-50 transition-colors disabled:opacity-60">
            <RefreshCw className={`w-3 h-3 ${loadingTemplate ? "animate-spin" : ""}`} />
            Load {org.domain || "Domain"} Template
          </button>
          <button data-testid="add-stage-button" onClick={addStage} className="flex items-center gap-1.5 bg-sage-600 text-white rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-sage-700 transition-colors">
            <Plus className="w-3 h-3" /> Add Stage
          </button>
        </div>
      </div>

      {stages.length === 0 ? (
        <div className="text-center py-12 bg-white border border-dashed border-sage-200 rounded-md">
          <p className="text-sage-400 text-sm mb-3">No stages configured. Load a template or add stages manually.</p>
          <button onClick={loadTemplate} className="bg-sage-600 text-white rounded-md px-4 py-2 text-xs font-semibold hover:bg-sage-700 transition-colors">Load Domain Template</button>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr className="bg-sage-50">
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-36">Stage Name</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-20">Yield %</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-16">AHT</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-28">Type</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-24">Priority</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-16">Local</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-16">Expat</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-16">Total</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-16">Util%</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-16">Prod%</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-20">Req FTE</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-24">Delta</th>
                  <th className="text-center py-2 px-2 text-xs uppercase tracking-wider text-sage-500 font-semibold border-b border-sage-100 w-28">Localisation</th>
                  <th className="py-2 px-2 border-b border-sage-100 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {stagesWithCalc.map((stage, idx) => {
                  const total = (stage.local_count || 0) + (stage.expat_count || 0);
                  const delta = total - stage.reqFTE;
                  return (
                    <tr key={stage.id || idx} className="border-b border-sage-50 hover:bg-sage-50/50 transition-colors" data-testid={`stage-row-${idx}`}>
                      <td className="py-1.5 px-3">
                        <input
                          value={stage.stage_name || ""}
                          onChange={(e) => updateStage(idx, "stage_name", e.target.value)}
                          className="w-full border-0 bg-transparent text-sm text-sage-800 focus:outline-none focus:ring-1 focus:ring-sage-300 rounded px-1 py-0.5"
                          data-testid={`stage-name-${idx}`}
                        />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" value={stage.yield_pct || 100} onChange={(e) => updateStage(idx, "yield_pct", Number(e.target.value))} min={1} max={100} className="w-full text-center border border-sage-100 rounded px-1 py-0.5 text-xs bg-white focus:border-sage-400 outline-none" data-testid={`stage-yield-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" value={stage.aht || 10} onChange={(e) => updateStage(idx, "aht", Number(e.target.value))} min={0.5} step={0.5} className="w-full text-center border border-sage-100 rounded px-1 py-0.5 text-xs bg-white focus:border-sage-400 outline-none" data-testid={`stage-aht-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <select value={stage.stage_type || "Front Office"} onChange={(e) => updateStage(idx, "stage_type", e.target.value)} className="w-full text-xs border border-sage-100 rounded px-1 py-0.5 bg-white focus:border-sage-400 outline-none">
                          {STAGE_TYPES.map((t) => <option key={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <select value={stage.priority || "Medium"} onChange={(e) => updateStage(idx, "priority", e.target.value)} className="w-full text-xs border border-sage-100 rounded px-1 py-0.5 bg-white focus:border-sage-400 outline-none">
                          {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                        </select>
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" value={stage.local_count || 0} onChange={(e) => updateStage(idx, "local_count", Math.max(0, Number(e.target.value)))} min={0} className="w-full text-center border border-sage-100 rounded px-1 py-0.5 text-xs bg-white focus:border-sage-400 outline-none" data-testid={`stage-local-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" value={stage.expat_count || 0} onChange={(e) => updateStage(idx, "expat_count", Math.max(0, Number(e.target.value)))} min={0} className="w-full text-center border border-sage-100 rounded px-1 py-0.5 text-xs bg-white focus:border-sage-400 outline-none" data-testid={`stage-expat-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2 text-center text-sm font-semibold text-sage-700">{total}</td>
                      <td className="py-1.5 px-2">
                        <input type="number" value={stage.utilization || 75} onChange={(e) => updateStage(idx, "utilization", Math.min(100, Math.max(40, Number(e.target.value))))} min={40} max={100} className="w-full text-center border border-sage-100 rounded px-1 py-0.5 text-xs bg-white focus:border-sage-400 outline-none" />
                      </td>
                      <td className="py-1.5 px-2">
                        <input type="number" value={stage.productivity || 80} onChange={(e) => updateStage(idx, "productivity", Math.min(130, Math.max(40, Number(e.target.value))))} min={40} max={130} className="w-full text-center border border-sage-100 rounded px-1 py-0.5 text-xs bg-white focus:border-sage-400 outline-none" />
                      </td>
                      <td className="py-1.5 px-2 text-center font-heading text-sm font-semibold text-sage-900" data-testid={`stage-req-fte-${idx}`}>
                        {stage.reqFTE.toFixed(1)}
                      </td>
                      <td className="py-1.5 px-2 text-center" data-testid={`stage-delta-${idx}`}>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${delta > 0.5 ? "bg-amber-100 text-amber-700" : delta < -0.5 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                          {delta > 0 ? "+" : ""}{delta.toFixed(1)}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-xs text-sage-500">{stage.loc.fteCountBasis}%</span>
                          <FlagChip flag={stage.loc.flag} diff={stage.loc.diff} />
                        </div>
                      </td>
                      <td className="py-1.5 px-2">
                        <button onClick={() => removeStage(idx)} className="text-sage-300 hover:text-red-500 transition-colors" data-testid={`delete-stage-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* Totals footer */}
              <tfoot>
                <tr className="bg-sage-50 border-t-2 border-sage-200">
                  <td className="py-2 px-3 text-xs font-semibold text-sage-700">TOTALS</td>
                  <td className="py-2 px-2 text-center text-xs text-sage-500">{avgYield}% avg</td>
                  <td className="py-2 px-2 text-center text-xs text-sage-400">—</td>
                  <td className="py-2 px-2"></td>
                  <td className="py-2 px-2"></td>
                  <td className="py-2 px-2 text-center text-xs font-semibold text-sage-700">{totalLocalCount}</td>
                  <td className="py-2 px-2 text-center text-xs font-semibold text-sage-700">{totalExpatCount}</td>
                  <td className="py-2 px-2 text-center text-xs font-semibold text-sage-700">{totalTeam}</td>
                  <td className="py-2 px-2"></td>
                  <td className="py-2 px-2"></td>
                  <td className="py-2 px-2 text-center font-heading text-sm font-bold text-sage-900">{totalReqFTE.toFixed(1)}</td>
                  <td className="py-2 px-2 text-center">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${overallDelta > 0.5 ? "bg-amber-100 text-amber-700" : overallDelta < -0.5 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                      {overallDelta > 0 ? "+" : ""}{overallDelta.toFixed(1)}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span className="text-xs text-sage-500">{overallLocalPct}% local</span>
                  </td>
                  <td className="py-2 px-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 text-xs text-sage-400">
        <Info className="w-3 h-3" />
        <span>Yield % is a throughput model — stages do not need to sum to 100%. AHT in minutes. Delta = Current Team − Required FTE.</span>
      </div>
    </div>
  );
}
